/**
 * src/api/observe.ts
 *
 * Typed wrappers for /v1/observe/*. Every function here is a thin
 * adapter — no business logic. The hooks in pages/* use these directly
 * via TanStack Query.
 */

import { request } from './client';
import type {
  AlertRecord,
  ScraperRecord,
  HealthSnapshot,
  RuntimeSnapshot,
  HealerAction,
  RunsPerDay,
  EffectiveRuntimeConfig,
} from '@/types';

// ── Health ───────────────────────────────────────────────────────────────────
export function getHealth(): Promise<HealthSnapshot> {
  return request<HealthSnapshot>('/health');
}

// How responsive otr-core is: event-loop delay + bot update timings, last hour.
export function getRuntime() {
  return request<{ ok: true; runtime: RuntimeSnapshot }>('/v1/observe/runtime');
}

// ── Alerts ───────────────────────────────────────────────────────────────────
export function listAlerts(opts: { limit?: number; severity?: string; category?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.severity) q.set('severity', opts.severity);
  if (opts.category) q.set('category', opts.category);
  const qs = q.toString();
  return request<{ ok: true; alerts: AlertRecord[] }>(`/v1/observe/alerts${qs ? '?' + qs : ''}`);
}

// ── Scrapers ─────────────────────────────────────────────────────────────────
export function listScrapers() {
  return request<{ ok: true; scrapers: ScraperRecord[] }>('/v1/observe/scrapers');
}

export function setScraperEnabled(id: string, enabled: boolean) {
  return request<{ ok: true; scraper: ScraperRecord }>(
    `/v1/observe/scrapers/${encodeURIComponent(id)}/toggle`,
    { method: 'POST', body: { enabled } },
  );
}

export function setScraperSchedule(id: string, runs_per_day: RunsPerDay) {
  return request<{ ok: true; scraper: ScraperRecord }>(
    `/v1/observe/scrapers/${encodeURIComponent(id)}/schedule`,
    { method: 'POST', body: { runs_per_day } },
  );
}

export function runScraperNow(id: string) {
  return request<{ ok: true }>(`/v1/observe/scrapers/${encodeURIComponent(id)}/run`, {
    method: 'POST',
  });
}

export function deleteScraper(id: string) {
  return request<{ ok: true; id: string }>(`/v1/observe/scrapers/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

// ── Runtime config (per-scraper search_terms + max_pages override) ──────────

/** GET — returns the effective config (registry override or env-fallback)
 *  along with both raw values so the UI can show "(env)" / "(override)"
 *  next to each row. */
export function getScraperRuntimeConfig(id: string) {
  return request<{ ok: true } & EffectiveRuntimeConfig>(
    `/v1/observe/scrapers/${encodeURIComponent(id)}/runtime-config`,
  );
}

/** POST — write the override. Pass `null` (or `[]` for terms) to
 *  clear the override and fall back to env. Omitted fields are left
 *  alone — useful for editing one without touching the other. */
export function updateScraperRuntimeConfig(
  id: string,
  patch: {
    search_terms?: string[] | null;
    max_pages?:    number   | null;
    base_url?:     string   | null;
  },
) {
  return request<{ ok: true; scraper: ScraperRecord; effective: EffectiveRuntimeConfig }>(
    `/v1/observe/scrapers/${encodeURIComponent(id)}/runtime-config`,
    { method: 'POST', body: patch },
  );
}

// ── Bulk runtime-config (fleet-wide one-shot) ───────────────────────────────

export type BulkScope =
  | 'all'
  | 'enabled'
  | 'cf_workers'
  | 'docker'
  | { ids: string[] };

export interface BulkRuntimeConfigPayload {
  search_terms?: string[] | null;
  max_pages?:    number   | null;
  scope?:        BulkScope;
}

export interface BulkRuntimeConfigResult {
  total:     number;
  succeeded: number;
  failed:    Array<{ id: string; error: string }>;
  results:   Array<{ id: string; ok: boolean; error?: string }>;
}

/** POST — apply a single search_terms / max_pages update across a
 *  resolved scope. Server validates the patch per-scraper; failures
 *  surface in the `failed` array rather than aborting the bulk. */
export function bulkUpdateRuntimeConfig(payload: BulkRuntimeConfigPayload) {
  return request<{ ok: true } & BulkRuntimeConfigResult>(
    '/v1/observe/scrapers/runtime-config/bulk',
    { method: 'POST', body: payload },
  );
}

// ── Healer ───────────────────────────────────────────────────────────────────
export function listHealerActions(limit = 50) {
  return request<{ ok: true; actions: HealerAction[] }>(`/v1/observe/healer/actions?limit=${limit}`);
}

export function triggerHealer(target: string, strategy: string) {
  return request<{ ok: true; action: HealerAction }>('/v1/observe/healer/trigger', {
    method: 'POST',
    body: { target, strategy },
  });
}
